/**
 * Service worker registration + update prompt for the Hellofoundation PWA.
 *
 * Behaviour:
 *   - Registers /service-worker.js on load (production only — SW in dev breaks CRA HMR).
 *   - When a NEW service worker installs (deploy shipped), we surface a small toast so the
 *     user can hit "Refresh" to activate it. We don't force-refresh — that would clobber
 *     an in-progress video call or mid-typed message.
 *   - Localhost path uses a permissive check so `yarn build && serve -s build` works locally.
 */
import { toast } from "sonner";
import { logger } from "./lib/logger";

const isLocalhost = Boolean(
  typeof window !== "undefined"
  && (window.location.hostname === "localhost"
    || window.location.hostname === "[::1]"
    || /^127(?:\.\d{1,3}){3}$/.test(window.location.hostname)),
);

export function registerServiceWorker() {
  // Never register during development (HMR + SW = broken)
  if (process.env.NODE_ENV !== "production") return;
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  const swUrl = "/service-worker.js";

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        registration.onupdatefound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.onstatechange = () => {
            if (installing.state === "installed") {
              if (navigator.serviceWorker.controller) {
                // We have an OLD SW controlling the page + a NEW one waiting. Tell the user.
                logger.info("PWA: new version available");
                toast.info("A new version of Hellofoundation is ready.", {
                  duration: 15000,
                  action: {
                    label: "Refresh",
                    onClick: () => {
                      registration.waiting?.postMessage("SKIP_WAITING");
                      // Reload as soon as the new SW takes control (activated)
                      navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
                    },
                  },
                });
              } else {
                // First install — content cached for offline. Silent.
                logger.info("PWA: cached for offline use");
              }
            }
          };
        };
      })
      .catch((err) => {
        if (isLocalhost) {
          logger.debug("SW register skipped on localhost:", err?.message);
        } else {
          logger.warn("SW register failed:", err?.message);
        }
      });
  });
}

export function unregisterServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch((err) => logger.debug("SW unregister failed:", err?.message));
}
